import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { endpointInterface } from 'services/endpointInterface/endpointInterface';
import { toast } from 'react-toastify';
import { LoginForm as textLang } from 'staticData/languages';
import AppContext from 'context/Context';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import Flex from 'components/common/Flex';
import { useHistory, useLocation } from 'react-router-dom';
import { gtm_event_userRegistered } from 'services/externals/google/googleTagManager';
import { hs_sendMailAddress } from 'services/externals/hubspot';
import { identifyMixpanelUser, trackEvent } from 'services/externals/mixpanel';
import { toltSignup } from 'services/externals/tolt';
import appleLoginButton from 'assets/img/hodlie/apple-login-test-button.png';
import { getFromLocalStorage } from 'staticData/common';
import 'assets/scss/custom/login.scss';
import 'assets/scss/custom/iosGoogleButton.scss';
import { breakpoints } from 'helpers/utils';

const LoginForm = ({ hasLabel, getInfo }) => {
  const {
    config: { lang }
  } = useContext(AppContext);

  // Stato per platform con controllo periodico su mobile
  const [platform, setPlatform] = useState(getFromLocalStorage('pwa_env'));

  // function to check if the user is on mobile
  const isMobile = () => {
    return window.innerWidth <= breakpoints.md;
  };

  // Effect to check the platform periodically on mobile
  useEffect(() => {
    if (!isMobile() || platform) return; // Do nothing if not on mobile or if platform is already set

    const interval = setInterval(() => {
      const currentPlatform = getFromLocalStorage('pwa_env');
      if (currentPlatform) {
        setPlatform(currentPlatform);
        clearInterval(interval); // Stop the polling once the value is found
      }
    }, 100);

    // Cleanup the interval when the component is unmounted
    return () => clearInterval(interval);
  }, [platform]);

  const location = useLocation();
  const fullPath = location.pathname + location.search;
  const queryParameters = new URLSearchParams(window.location.search);
  const referralInURL = queryParameters.get('ref') || '';
  const utm_campaign = queryParameters.get('utm_campaign')
    ? queryParameters.get('utm_campaign').trim()
    : '';

  // State
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const routerHistory = useHistory();
  const [loadingNativeButton, setLoadingNativeButton] = useState();

  const handleSubmit = async e => {
    e.preventDefault();

    // Generate Recaptcha Token & Login
    window.grecaptcha.enterprise.ready(async () => {
      const recaptcha_token = await window.grecaptcha.enterprise.execute(
        '6LcE9igoAAAAAJ2plIdhi4bvhKr3hn95kyNmxTqc',
        { action: 'login' }
      );

      let form = new FormData();
      form.append('username', formData['username'].toLowerCase());
      form.append('password', formData['password']);
      form.append('recaptcha_token', recaptcha_token);
      let loginResponse = await endpointInterface(
        lang,
        'backend',
        'loginPage',
        'post',
        true,
        form
      );
      if (loginResponse.validResponse) {
        // Send mail address to hubspot
        hs_sendMailAddress(
          loginResponse.data?.email || formData['username'].toLowerCase(),
          fullPath
        );
        // Identify user in mixpanel
        identifyMixpanelUser(
          loginResponse.data?.tracking_id || 0,
          loginResponse.data?.email || formData['username']
        );
        await getInfo();
        routerHistory.push('/');
      } else {
        console.log('loginResponse', loginResponse, loginResponse.errorID);
        if (loginResponse.errorID === 'login_subcustody_mismatch') {
          localStorage.setItem('migration_completed', true);
          routerHistory.push('/migration-completed');
          return;
        }
        toast.error(loginResponse.responseMessage, { closeButton: false });
        setTimeout(() => {
          toast.dismiss();
        }, 5000);
      }
    });
  };

  // Function called by the old iOS app when executing the Google login
  const iosGoogleLogin = token => {
    const responseObject = { credential: token };
    sendLoginToken(responseObject);
  };

  window.iosGoogleLogin = iosGoogleLogin;

  const sendLoginToken = async (response, route = 'googleLogin') => {
    setLoadingNativeButton(route.replace('Login', ''));
    let token = response['credential'];
    let form = new FormData();
    form.append('token', token);
    form.append('referral', referralInURL.trim());
    form.append('utm_campaign', utm_campaign);
    form.append('tolt_referral', window.tolt_referral || '');
    let loginResponse = await endpointInterface(
      lang,
      'backend',
      route,
      'post',
      true,
      form
    );
    let userWasRegistered = false;
    if (
      loginResponse.data.already_registered != undefined &&
      loginResponse.data.already_registered === true
    )
      userWasRegistered = true;
    if (loginResponse.validResponse) {
      let data = await getInfo(true);
      // Send mail address to hubspot
      hs_sendMailAddress(loginResponse.data?.email || '', fullPath);
      if (!userWasRegistered) {
        // Send the user_registered GTM event
        gtm_event_userRegistered(data?.tracking_id || null);
        // send event in mixpanel
        trackEvent('signup', {
          method: 'google',
          mail: loginResponse.data?.email || formData.email
        });
        // send registration data to tolt
        toltSignup(loginResponse.data?.email || formData.email);
      }
      // identify user in mixpanel
      identifyMixpanelUser(
        loginResponse.data?.tracking_id || 0,
        loginResponse.data?.email || formData.email
      );
      setLoadingNativeButton(null);
      routerHistory.push('/');
    } else {
      if (loginResponse.errorID === 'login_subcustody_mismatch') {
        localStorage.setItem('migration_completed', true);
        routerHistory.push('/migration-completed');
        return;
      }
      setLoadingNativeButton(null);
      toast.error(loginResponse.responseMessage, { closeButton: false });
      setTimeout(() => {
        toast.dismiss();
      }, 5000);
    }
  };

  window.sendLoginToken = sendLoginToken;

  const handleFieldChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // TODO: Get google client id from config file

  const requestTokenFromMaui = (e, signInType) => {
    e.preventDefault();
    // trigger SignIn
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.mauiBridge
    ) {
      // iOS
      if (signInType === 'APPLE')
        window.webkit.messageHandlers.mauiBridge.postMessage(
          'getAppleSignInToken'
        );
      // Android
      if (signInType === 'GOOGLE')
        window.webkit.messageHandlers.mauiBridge.postMessage(
          'getGoogleSignInToken'
        );
    }
  };

  return (
    <Form onSubmit={e => handleSubmit(e)}>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>{textLang.email[lang]}</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? textLang.email[lang] : ''}
          value={formData.username}
          name="username"
          onChange={handleFieldChange}
          type="text"
        />
      </Form.Group>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? 'Password' : ''}
          value={formData.password}
          name="password"
          onChange={handleFieldChange}
          type="password"
        />
      </Form.Group>
      <Flex className="justify-content-center">
        <Link className="fs--1 mb-0" to={`/forgot-password`}>
          {textLang.forgotPwd[lang]}
        </Link>
      </Flex>
      <Form.Group>
        <Flex className="justify-content-center">
          <Button
            type="submit"
            color="primary"
            className="mt-3 custom-button"
            disabled={!formData.username || !formData.password}
          >
            {textLang.login[lang]}
          </Button>
        </Flex>
      </Form.Group>
      <Flex className="justify-content-center mt-3 mb-3">
        {textLang.or[lang]}
      </Flex>
      <div className="d-flex justify-content-center">
        {platform === 'ios' ? (
          <button
            className="gsi-material-button"
            style={{ width: '250px' }}
            onClick={e => {
              e.preventDefault();
              if (
                window.webkit &&
                window.webkit.messageHandlers &&
                window.webkit.messageHandlers['trigger-google-signin']
              ) {
                window.webkit.messageHandlers[
                  'trigger-google-signin'
                ].postMessage(null);
              } else {
                console.error('Google Sign-In handler is not available.');
              }
            }}
          >
            <div className="gsi-material-button-state"></div>
            <div className="gsi-material-button-content-wrapper">
              <div className="gsi-material-button-icon">
                <svg
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  style={{ display: 'block' }}
                >
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  ></path>
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  ></path>
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                  ></path>
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  ></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
              </div>
              <span className="gsi-material-button-contents">
                {textLang.googleButtonText[lang]}
              </span>
              <span style={{ display: 'none' }}>
                {textLang.googleButtonText[lang]}
              </span>
            </div>
          </button>
        ) : (
          platform !== 'ANDROID' &&
          platform !== 'IOS' && (
            <GoogleOAuthProvider clientId="963417905135-ho3o0v3h0lurjmgp9kgkdqfunai4k1nm.apps.googleusercontent.com">
              <GoogleLogin
                text="signin_with"
                onSuccess={sendLoginToken}
                onError={error => toast.error(error, { closeButton: false })}
                width="250px"
                useOneTap
              />
            </GoogleOAuthProvider>
          )
        )}
      </div>

      {/* APPLE LOGIN for new iOS APP version */}
      {platform === 'IOS' && (
        <div className="d-flex justify-content-center mb-3">
          {loadingNativeButton === 'apple' ? (
            <Spinner animation="border" role="status" />
          ) : (
            <img
              src={appleLoginButton}
              className="custom-apple-signin-button pointerCursor"
              onClick={e => requestTokenFromMaui(e, 'APPLE')}
            />
          )}
        </div>
      )}
      {/* google login button for native new app */}
      {(platform === 'ANDROID' || platform === 'IOS') && (
        <Flex className="justify-content-center">
          {loadingNativeButton === 'google' ||
          window.location.pathname === '/login-google' ? (
            <Spinner animation="border" role="status" />
          ) : (
            <button
              id="google-login-button"
              className="gsi-material-button"
              style={{ width: '250px' }}
              onClick={e =>
                platform === 'IOS'
                  ? requestTokenFromMaui(e, 'GOOGLE')
                  : routerHistory.push('/login-google')
              }
            >
              <div className="gsi-material-button-state"></div>
              <div className="gsi-material-button-content-wrapper">
                <div className="gsi-material-button-icon">
                  <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    style={{ display: 'block' }}
                  >
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                    ></path>
                    <path
                      fill="#4285F4"
                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    ></path>
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    ></path>
                    <path
                      fill="#34A853"
                      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    ></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                  </svg>
                </div>
                <span className="gsi-material-button-contents">
                  {textLang.googleButtonText[lang]}
                </span>
                <span style={{ display: 'none' }}>
                  {textLang.googleButtonText[lang]}
                </span>
              </div>
            </button>
          )}
        </Flex>
      )}
    </Form>
  );
};

LoginForm.propTypes = {
  hasLabel: PropTypes.bool,
  getInfo: PropTypes.func
};

export default LoginForm;
